import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f1f4ac20"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "sm:w-22rem" }
const _hoisted_2 = {
  key: 0,
  class: "flex text-sm align-items-center justify-content-between mb-s3"
}
const _hoisted_3 = { class: "text-bolder" }
const _hoisted_4 = { class: "wallet-panel-data" }
const _hoisted_5 = { class: "flex text-sm align-items-center justify-content-between" }
const _hoisted_6 = { class: "caption" }
const _hoisted_7 = { class: "wallet-address-panel px-s4 mt-s2" }
const _hoisted_8 = { class: "mr-s3" }
const _hoisted_9 = {
  key: 0,
  class: "flex text-sm mt-s4 justify-content-between"
}
const _hoisted_10 = { class: "text-muted" }
const _hoisted_11 = {
  key: 1,
  class: "flex text-sm mt-s2 justify-content-between"
}
const _hoisted_12 = { class: "text-muted" }
const _hoisted_13 = { class: "flex align-items-center" }
const _hoisted_14 = { class: "text-bolder" }
const _hoisted_15 = { class: "flex text-sm align-items-center justify-content-between mt-s3" }
const _hoisted_16 = { class: "caption" }
const _hoisted_17 = { class: "flex text-sm align-items-center mt-s1" }
const _hoisted_18 = { class: "connector-icon mr-s1" }
const _hoisted_19 = ["src", "alt"]

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BigNumber from 'bignumber.js';
import { NATIVE_TOKEN_SYMBOL } from '@/helpers/constants';
import CopyIcon from '@/components/icon/CopyIcon';
import ExternalLink from '@/components/ExternalLink';
import { ChainId } from '@/sdk/constants';
import { SUPPORTED_NETWORK_MODE } from '@/constants/SUPPORTED_NETWORK_MODE';
import { TRANSACTION_FEE_MINIMUM } from '@/constants/TRANSACTION_FEE_NATIVE';
import { WRAPPED_MILKOMEDA_CARDANO_NETWORK_NAME } from '@/constants/NETWORK_NAMES';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { getScanLink } from '@/sdk/utils';
import { format } from '@/utils/format';
import { switchNetwork } from '@/utils/blockchain';
import InfoMessage from '@/components/InfoMessage';
import { SELECTED_NETWORK_NAME, DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { NETWORK_ETH_TOKEN_NAME, CURRENT_NATIVE_TOKEN_ADDRESS } from '@/helpers/network-eth-token';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { useWallet } from '@/store/modules/wallet/useWallet';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useBalances } from '@/store/modules/tokens/useBalances';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'WalletData',
  emits: ["disconnectWallet"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'disconnectWallet'): void;
}), expose: any, slots: any, attrs: any }) {

const {
  walletState,
  getSupportedNetworksNames,
  getFirstSupportedNetwork,
  getWalletName,
  getWalletNetwork,
} = useWallet();
const { getGasToken, getTokensWithFilter } = useTokens();
const { getBalanceByToken, getBalancesWithFilter, balanceByTokenSymbolAndChainId } = useBalances();
const { t } = useI18n();



const isBalanceTooltip = computed(() => ENABLE_FAKE_CARDANO_NETWORK);
const walletData = computed<{
  name: string;
  networkName: string;
  isNetworkSupported: boolean | null | undefined;
} | null>(() => {
  return walletState.isInjected
    ? {
        name: getWalletName,
        networkName: getWalletNetwork?.shortName,
        isNetworkSupported: walletState.isNetworkSupported,
      }
    : null;
});
const contractAddress = CURRENT_NATIVE_TOKEN_ADDRESS;

const walletConnector = computed(() => walletState.wallets[SELECTED_NETWORK_NAME].connector);

const baseTokenBalance = computed(() => {
  const rawBalance = getBalancesWithFilter(getGasToken())?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});
const baseTokenBalanceInCardano = computed(() => {
  const symbol = getGasToken().symbol as string;
  const rawBalance = balanceByTokenSymbolAndChainId(symbol, +DEFAULT_CARDANO_CHAIN_ID).value
    ?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});
const baseTokenBalanceInMilkomeda = computed(() => {
  const symbol = getGasToken().symbol as string;
  const rawBalance = balanceByTokenSymbolAndChainId(symbol, +DEFAULT_NETWORK_ID!).value?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});

const nativeTokenBalance = computed(() => {
  const symbol = NATIVE_TOKEN_SYMBOL;
  const rawBalance = getBalanceByToken(getTokensWithFilter({ symbol }))?.balance;
  return new BigNumber(rawBalance?.toFixed() || 0);
});

const explorerLink = computed(() => {
  const networkName = ENABLE_FAKE_CARDANO_NETWORK
    ? WRAPPED_MILKOMEDA_CARDANO_NETWORK_NAME
    : SELECTED_NETWORK_NAME;
  return getScanLink(
    walletData.value?.name as string,
    'wallet',
    ChainId[`${networkName.toUpperCase()}_${SUPPORTED_NETWORK_MODE}`],
  );
});
const isBaseTokenBalanceTooLow = computed(() => baseTokenBalance.value.lt(TRANSACTION_FEE_MINIMUM));
const isNetSupported = computed(() => walletData.value?.isNetworkSupported);
const currency = computed(() => NETWORK_ETH_TOKEN_NAME);
const supportedNetwork = computed(() => getSupportedNetworksNames.split(',')[0]);

const onSwitchNetwork = () => {
  switchNetwork(getFirstSupportedNetwork);
};

const onDisconnectWallet = () => {
  emits('disconnectWallet');
};
const walletName = () => {
  return walletData.value?.name.slice(0, 16) + '...' + walletData.value?.name.slice(-4);
};

const formatGazTokenBalance = tokenBalance => {
  const tokenBalanceRounded = tokenBalance.toFixed(4, BigNumber.ROUND_DOWN).replace(',', '');
  const isSmallBalance = tokenBalance.lt(0.0001);
  const isZeroBalance = tokenBalance.isEqualTo(0);
  return isSmallBalance && !isZeroBalance ? '< 0.0001' : format(tokenBalanceRounded, 'unlimited');
};
const formatNativeTokenBalance = () => {
  const tokenBalanceRounded = nativeTokenBalance.value
    .toFixed(2, BigNumber.ROUND_DOWN)
    .replace(',', '');
  const isSmallBalance = nativeTokenBalance.value.lt(0.01);
  const isZeroBalance = nativeTokenBalance.value.isEqualTo(0);
  return isSmallBalance && !isZeroBalance ? '< 0.01' : format(tokenBalanceRounded, 'unlimited');
};

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (!_unref(isNetSupported))
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(InfoMessage), {
              type: "danger",
              size: "16"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('error.network.wrongNetwork')), 1),
                _createTextVNode(" " + _toDisplayString(_unref(t)('error.network.switchNetwork', { supportedNetwork: _unref(supportedNetwork) })), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_Button, {
              label: _unref(t)('switch'),
              class: "ml-auto button-base",
              onClick: onSwitchNetwork
            }, null, 8, ["label"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)('wallet.yourAddress')), 1),
          _createVNode(_unref(ExternalLink), {
            label: _unref(t)('viewInExplorer'),
            url: _unref(explorerLink)
          }, null, 8, ["label", "url"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, _toDisplayString(walletName()), 1),
          _createVNode(_unref(CopyIcon), {
            data: _unref(walletData)?.name,
            class: "text-muted"
          }, null, 8, ["data"])
        ]),
        (_unref(isNetSupported))
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('wallet.lqfBalance')), 1),
              _createElementVNode("div", null, _toDisplayString(formatNativeTokenBalance()), 1)
            ]))
          : _createCommentVNode("", true),
        (_unref(isNetSupported))
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(t)('wallet.currencyBalance', { currency: _unref(currency) })), 1),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("span", null, _toDisplayString(formatGazTokenBalance(_unref(baseTokenBalance))), 1),
                (_unref(isBalanceTooltip))
                  ? (_openBlock(), _createBlock(TooltipIcon, {
                      key: 0,
                      class: "ml-s1"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", null, [
                          _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Cardano' })) + ": ", 1),
                          _createElementVNode("span", null, _toDisplayString(formatGazTokenBalance(_unref(baseTokenBalanceInCardano))), 1)
                        ]),
                        _createElementVNode("div", null, [
                          _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Milkomeda' })) + ": ", 1),
                          _createElementVNode("span", null, _toDisplayString(formatGazTokenBalance(_unref(baseTokenBalanceInMilkomeda))), 1)
                        ])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true),
        (_unref(isBaseTokenBalanceTooLow) && _unref(isNetSupported))
          ? (_openBlock(), _createBlock(_unref(InfoMessage), {
              key: 2,
              type: "warning",
              size: "12",
              "as-panel": "",
              class: "mt-s3"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(t)('wallet.lowCurrencyBalance', { currency: _unref(currency) })), 1),
                  _createElementVNode("div", null, _toDisplayString(_unref(t)('wallet.youNeedCurrency', { currency: _unref(currency) })), 1)
                ])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(t)('wallet.connected')), 1),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("img", {
              src: _unref(walletConnector).icon,
              alt: _unref(walletConnector).name
            }, null, 8, _hoisted_19)
          ]),
          _createElementVNode("span", null, _toDisplayString(_unref(walletConnector).name), 1)
        ])
      ])
    ]),
    _createVNode(_component_Button, {
      label: _unref(t)('wallet.disconnect'),
      class: "w-full ml-auto button-base mt-s3",
      onClick: onDisconnectWallet
    }, null, 8, ["label"])
  ], 64))
}
}

})